/**
 * Analytics Service
 * 
 * Handles event batching and sending to the backend /analytics/events endpoint.
 * Events are queued and sent in batches for efficiency.
 */

const API_URL = process.env.REACT_APP_API_URL;
const MAX_BATCH_SIZE = 50;
const FLUSH_INTERVAL_MS = 10000; // Flush every 10 seconds

interface AnalyticsEvent {
  event_type: string;
  client_timestamp: string;
  properties: Record<string, unknown>;
}

interface EventPayload {
  events: AnalyticsEvent[];
}

class AnalyticsService {
  private eventQueue: AnalyticsEvent[] = [];
  private accessToken: string | null = null;
  private flushIntervalId: ReturnType<typeof setInterval> | null = null;
  private isInitialized: boolean = false;

  /**
   * Initialize the analytics service with an access token
   */
  init(accessToken: string): void {
    this.accessToken = accessToken;
    this.isInitialized = true;
    
    // Start periodic flush
    if (!this.flushIntervalId) {
      this.flushIntervalId = setInterval(() => {
        this.flush();
      }, FLUSH_INTERVAL_MS);
    }

    // Setup page unload handlers for reliable session end tracking
    this.setupUnloadHandlers();
  }

  /**
   * Update the access token (e.g., after token refresh)
   */
  setAccessToken(token: string): void {
    this.accessToken = token;
  }

  /**
   * Track an analytics event
   */
  track(eventType: string, properties: Record<string, unknown> = {}): void {
    if (!this.isInitialized) {
      console.warn('[Analytics] Service not initialized. Call init() first.');
      return;
    }

    const event: AnalyticsEvent = {
      event_type: eventType,
      client_timestamp: new Date().toISOString(),
      properties
    };

    this.eventQueue.push(event);

    // Auto-flush if batch size reached
    if (this.eventQueue.length >= MAX_BATCH_SIZE) {
      this.flush();
    }
  }

  /**
   * Flush all queued events to the backend
   */
  async flush(): Promise<void> {
    if (this.eventQueue.length === 0 || !this.accessToken) {
      return;
    }

    // Take all events from queue
    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    try {
      await this.sendEvents(eventsToSend);
    } catch (error) {
      // On failure, add events back to queue (at the front)
      console.error('[Analytics] Failed to send events:', error);
      this.eventQueue = [...eventsToSend, ...this.eventQueue];
      
      // Trim queue if it gets too large to prevent memory issues
      if (this.eventQueue.length > MAX_BATCH_SIZE * 2) {
        this.eventQueue = this.eventQueue.slice(-MAX_BATCH_SIZE);
      }
    }
  }

  /**
   * Flush events synchronously using fetch with keepalive (for page unload)
   * This is more reliable than sendBeacon because it supports Authorization headers
   */
  flushSync(): void {
    if (this.eventQueue.length === 0 || !this.accessToken) {
      return;
    }

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    const payload: EventPayload = { events: eventsToSend };
    const url = `${API_URL}/analytics/events`;
    
    // Use fetch with keepalive flag - this is the most reliable way to send
    // authenticated requests during page unload. The keepalive flag allows
    // the request to complete even after the page has been closed.
    try {
      // Note: keepalive requests have a 64KB limit, but our batches are small
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.accessToken}`
        },
        body: JSON.stringify(payload),
        keepalive: true
      });
      // Don't await - fire and forget for page unload scenarios
    } catch (error) {
      console.error('[Analytics] flushSync error:', error);
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    // Flush remaining events
    this.flushSync();
    
    // Clear interval
    if (this.flushIntervalId) {
      clearInterval(this.flushIntervalId);
      this.flushIntervalId = null;
    }

    // Remove unload handlers
    this.removeUnloadHandlers();
    
    this.isInitialized = false;
    this.accessToken = null;
  }

  /**
   * Send events to the backend API
   */
  private async sendEvents(events: AnalyticsEvent[]): Promise<void> {
    if (!this.accessToken) {
      throw new Error('No access token available');
    }

    const payload: EventPayload = { events };

    const response = await fetch(`${API_URL}/analytics/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.accessToken}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok && response.status !== 204) {
      throw new Error(`Analytics API error: ${response.status}`);
    }
  }

  /**
   * Setup handlers for page unload events
   */
  private setupUnloadHandlers(): void {
    // beforeunload - fired when page is about to unload
    window.addEventListener('beforeunload', this.handleUnload);
    
    // visibilitychange - fired when tab becomes hidden (more reliable on mobile)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // pagehide - better than unload, works with bfcache
    window.addEventListener('pagehide', this.handleUnload);
  }

  /**
   * Remove unload handlers
   */
  private removeUnloadHandlers(): void {
    window.removeEventListener('beforeunload', this.handleUnload);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.handleUnload);
  }

  /**
   * Handle page unload
   */
  private handleUnload = (): void => {
    this.flushSync();
  };

  /**
   * Handle visibility change (tab hidden/shown)
   */
  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.flushSync();
    }
  };
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

// Export types for use in other files
export type { AnalyticsEvent };

